import type { UseFormRegisterReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Flex, Text, TextArea } from '@enechain/ecloud-designsystem'

import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import type { EmissionLinkingFieldProps } from '~/pages/SpotBidding/components/EmissionLinkingField'
import { EmissionLinkingField } from '~/pages/SpotBidding/components/EmissionLinkingField'

// 社内限定の自由記述は 100 文字 (design `spot.html` の note-counter と同値)。
const MEMO_FREE_MAX_LEN = 100

type MemoFieldsProps = {
  /** id 衝突回避の prefix (= modal ごとに一意)。 */
  idPrefix: string
  freeValue: string
  registerFree: UseFormRegisterReturn
  freeError?: string
  /** 排出係数紐付け UI の状態 (= EmissionLinkingField へそのまま渡す)。 */
  emission: Omit<EmissionLinkingFieldProps, 'idPrefix' | 'freeTextCounter'>
  /**
   * JEPX自由記述欄への直接入力 (issue #6154)。 判別コメント (あれば) の後ろに
   * 連結して保存する (spec §7 機能詳細 4、 組み立ては呼び出し元が
   * `composeJepxNote` で行う)。 省略した呼び出し元 ([[SpotBidCreateModal]] /
   * [[BulkCreateModal]]) は従来どおり textarea を出さない
   * (= [[EmissionLinkingField]] の判別コメントのみ)。
   */
  jepxFree?: {
    value: string
    register: UseFormRegisterReturn
    error?: string
    /**
     * 自由記述の最大文字数 (`jepxFreeTextMaxLength`)。 紐付け ON なら判別コメント
     * の分を差し引いた値になり、 `maxLength` と文字数カウンタの両方に使う。
     */
    maxLength: number
    /** 会社情報の取得中など、 まだ入力させない間は true。 */
    disabled?: boolean
  }
}

/**
 * スポット通常入札のメモ入力群。
 * JEPX 提出用 (memo_jepx) は判別コメント (排出係数紐付け、 spec §7 AC-BSP-086) と
 * ユーザーの自由記述を連結して保存する (issue #6154。 直接入力欄は `jepxFree` を
 * 渡した呼び出し元のみ表示)。 社内限定のメモ（自由記述、 memo_free) は
 * textarea + 文字数カウンタのまま。
 * IndividualBidModal / SpotBidCreateModal から共有する。
 *
 * 「JEPX自由記述欄」 のグループ見出しは [[EmissionLinkingField]] 側が持つ
 * (判別コメントの格納先が `memo_jepx` = JEPX自由記述欄 のため)。
 */
export const MemoFields = ({
  idPrefix,
  freeValue,
  registerFree,
  freeError,
  emission,
  jepxFree,
}: MemoFieldsProps): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <>
      <EmissionLinkingField
        idPrefix={idPrefix}
        {...emission}
        freeTextCounter={
          jepxFree !== undefined
            ? { length: jepxFree.value.length, max: jepxFree.maxLength }
            : undefined
        }
      />
      {jepxFree !== undefined && (
        <FieldRoot alignItems="stretch" invalid={jepxFree.error !== undefined}>
          <TextArea
            aria-label={t('page:biddingSpot.form.memoJepx')}
            disabled={jepxFree.disabled}
            id={`${idPrefix}-memoJepxFree`}
            maxLength={jepxFree.maxLength}
            placeholder={t('page:biddingSpot.form.memoJepxPlaceholder')}
            rows={2}
            {...jepxFree.register}
          />
          <FieldErrorText>{jepxFree.error}</FieldErrorText>
        </FieldRoot>
      )}
      {/* DS `TextArea` は内部で縦 Stack に包む。 chakra の `Field.Root` は
          `align-items: flex-start` なので、 その Stack が伸びずに内容幅
          (実測 195px) で止まり、 中の textarea の `width: 100%` もそれに対して
          解決されて細くなる (2026-08-24 実測)。 素の chakra `Textarea` は
          Field.Root の直接の子だったのでこの現象が起きていなかった。
          stretch にして 8080 の `width:100%` と同じ全幅に戻す。 */}
      <FieldRoot alignItems="stretch" invalid={freeError !== undefined}>
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <FieldLabel htmlFor={`${idPrefix}-memoFree`}>
            {t('page:biddingSpot.form.memoFree')}
          </FieldLabel>
          <Text
            color="Text.lowEmphasis"
            fontSize="sm"
          >{`${freeValue.length} / ${MEMO_FREE_MAX_LEN}`}</Text>
        </Flex>
        {/* DS の textarea は **`TextArea` (大文字 A)**。 同じ package が
            `Textarea` (小文字 a) も export しているが、 そちらは chakra 素の
            re-export で DS のスタイルが 1 つも当たらない (2026-08-24 実測。
            デザイナー指摘で差し替え)。 DS は `min-height: 5rem` を持つので
            `rows={2}` は下回り側の指定として残るだけになる。
            */}
        <TextArea
          id={`${idPrefix}-memoFree`}
          maxLength={MEMO_FREE_MAX_LEN}
          rows={2}
          {...registerFree}
        />
        <Text marginTop="1" color="Text.lowEmphasis" fontSize="sm">
          {t('page:biddingSpot.form.memoFreeHint')}
        </Text>
        <FieldErrorText>{freeError}</FieldErrorText>
      </FieldRoot>
    </>
  )
}
